import Button from "components/ui/Button";
import {
	Bath,
	Bed,
	Calendar,
	Edit,
	Eye,
	MapPin,
	MessageCircle,
	Square,
} from "lucide-react";
import React, { useState } from "react";

interface SalesProperty {
	id: string;
	title: string;
	address: string;
	type: string;
	price: number;
	status: string;
	bedrooms: number;
	bathrooms: number;
	square_feet: number;
	images: string[];
	description: string;
	created_at: string;
	viewings: number;
	inquiries: number;
	viewingRequests?: number;
}

interface SalesTableProps {
	properties?: SalesProperty[];
	onPropertyAction: (property: SalesProperty, action: string) => void;
	onStatusUpdate: (propertyId: string, newStatus: string) => void;
	canEdit?: boolean; // Whether user can edit properties
}

const SalesTable: React.FC<SalesTableProps> = ({
	properties = [],
	onPropertyAction,
	onStatusUpdate,
	canEdit = true,
}) => {
	const [expandedProperty, setExpandedProperty] = useState<string | null>(null);
	const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

	// Helper function to validate if an image URL is a complete URL
	const isValidImageUrl = (url: string | undefined | null): boolean => {
		if (!url) return false;
		// Check if it's a full URL (http/https) or data URL
		return url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:");
	};

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<string, { class: string; label: string }> = {
			active: {
				class:
					"bg-success/20 text-success border border-success/30 dark:bg-success/30 dark:text-success dark:border-success/40",
				label: "Active",
			},
			pending: {
				class:
					"bg-warning/20 text-warning border border-warning/30 dark:bg-warning/30 dark:text-warning dark:border-warning/40",
				label: "Pending",
			},
			occupied: {
				class:
					"bg-primary/20 text-primary border border-primary/30 dark:bg-primary/30 dark:text-primary dark:border-primary/40",
				label: "Occupied",
			},
			withdrawn: {
				class: "bg-error/20 text-error border border-error/30 dark:bg-error/30 dark:text-error dark:border-error/40",
				label: "Withdrawn",
			},
		};

		const config = statusConfig[status] || {
			class: "bg-muted/30 text-foreground border border-border",
			label: status,
		};

		return (
			<span
				className={`px-2 py-1 rounded-full text-xs font-medium ${config?.class}`}
			>
				{config?.label}
			</span>
		);
	};

	const formatPrice = (price: number) => {
		const numPrice = Number(price) || 0;
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})?.format(numPrice);
	};

	const getDaysOnMarket = (created_at: string) => {
		const listed = new Date(created_at);
		const today = new Date();
		const diffTime = Math.abs(today.getTime() - listed.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	if (properties?.length === 0) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">
					No properties match your current filters.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Table with horizontal scroll on mobile */}
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
						<thead>
							<tr className="border-b border-border">
								<th className="text-left py-3 px-4 font-medium text-muted-foreground">
									Property
								</th>
								<th className="text-left py-3 px-4 font-medium text-muted-foreground">
									Price
								</th>
								<th className="text-left py-3 px-4 font-medium text-muted-foreground">
									Status
								</th>
								<th className="text-left py-3 px-4 font-medium text-muted-foreground">
									Days on Market
								</th>
								<th className="text-left py-3 px-4 font-medium text-muted-foreground">
									Viewings
								</th>
								<th className="text-left py-3 px-4 font-medium text-muted-foreground">
									Inquiries
								</th>
								<th className="text-right py-3 px-4 font-medium text-muted-foreground">
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{properties?.map((property) => (
								<tr
									key={property?.id}
									className="border-b border-border hover:bg-muted/50"
								>
									<td className="py-4 px-4">
										<div className="flex items-start gap-3">
											<div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
												{property?.images?.[0] &&
												 isValidImageUrl(property?.images?.[0]) &&
												 !property?.images?.[0]?.startsWith("data:") &&
												 !imageErrors.has(property.id) ? (
													<img
														src={property?.images?.[0]}
														alt={`${property?.title} - Property image`}
														className="w-full h-full object-cover"
														onError={() => {
															setImageErrors((prev) => new Set(prev).add(property.id));
														}}
													/>
												) : (
													<Square className="w-6 h-6 text-muted-foreground" />
												)}
											</div>
											<div>
												<h3 className="font-medium text-foreground">
													{property?.title}
												</h3>
												<div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
													<MapPin className="w-3 h-3" />
													<span>{property?.address}</span>
												</div>
												<div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
													<div className="flex items-center gap-1">
														<Bed className="w-3 h-3" />
														<span>{property?.bedrooms} bed</span>
													</div>
													<div className="flex items-center gap-1">
														<Bath className="w-3 h-3" />
														<span>{property?.bathrooms} bath</span>
													</div>
												</div>
											</div>
										</div>
									</td>
									<td className="py-4 px-4">
										<span className="font-semibold text-foreground">
											{formatPrice(property?.price)}
										</span>
									</td>
									<td className="py-4 px-4">
										{getStatusBadge(property?.status)}
									</td>
									<td className="py-4 px-4">
										<span className="text-foreground">
											{getDaysOnMarket(property?.created_at)} days
										</span>
									</td>
									<td className="py-4 px-4">
										<span className="text-foreground">
											{property?.viewings || 0}
										</span>
									</td>
									<td className="py-4 px-4">
										<span className="text-foreground">
											{property?.inquiries || 0}
										</span>
									</td>
									<td className="py-4 px-4">
										<div className="flex items-center justify-end gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => onPropertyAction(property, "view")}
												title="View Images"
											>
												<Eye className="w-4 h-4" />
											</Button>
											{canEdit && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => onPropertyAction(property, "edit")}
													title="Edit Property"
												>
													<Edit className="w-4 h-4" />
												</Button>
											)}
											<Button
												variant="ghost"
												size="sm"
												onClick={() => onPropertyAction(property, "schedule")}
												title="Schedule Viewing"
											>
												<Calendar className="w-4 h-4" />
											</Button>
											<Button
												variant="ghost"
												size="sm"
												onClick={() => onPropertyAction(property, "chat")}
												title="Live Chat"
											>
												<MessageCircle className="w-4 h-4" />
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
			{/* Mobile Cards */}
			<div className="lg:hidden space-y-4">
				{properties?.map((property) => (
					<div
						key={property?.id}
						className="bg-card border border-border rounded-lg p-4"
					>
						<div className="flex items-start gap-3 mb-3">
							<div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
								{property?.images?.[0] &&
								 isValidImageUrl(property?.images?.[0]) &&
								 !property?.images?.[0]?.startsWith("data:") &&
								 !imageErrors.has(property.id) ? (
									<img
										src={property?.images?.[0]}
										alt={`${property?.title} - Property image`}
										className="w-full h-full object-cover"
										onError={() => {
											setImageErrors((prev) => new Set(prev).add(property.id));
										}}
									/>
								) : (
									<Square className="w-6 h-6 text-muted-foreground" />
								)}
							</div>
							<div className="flex-1">
								<h3 className="font-medium text-foreground">
									{property?.title}
								</h3>
								<div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
									<MapPin className="w-3 h-3" />
									<span>{property?.address}</span>
								</div>
								<div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
									<div className="flex items-center gap-1">
										<Bed className="w-3 h-3" />
										<span>{property?.bedrooms} bed</span>
									</div>
									<div className="flex items-center gap-1">
										<Bath className="w-3 h-3" />
										<span>{property?.bathrooms} bath</span>
									</div>
								</div>
							</div>
						</div>

						<div className="flex items-center justify-between mb-3">
							<span className="font-semibold text-lg text-foreground">
								{formatPrice(property?.price)}
							</span>
							{getStatusBadge(property?.status)}
						</div>

						<div className="grid grid-cols-3 gap-4 text-sm mb-4">
							<div>
								<span className="text-muted-foreground">Days on Market</span>
								<p className="font-medium text-foreground">
									{getDaysOnMarket(property?.created_at)}
								</p>
							</div>
							<div>
								<span className="text-muted-foreground">Viewings</span>
								<p className="font-medium text-foreground">
									{property?.viewings || 0}
								</p>
							</div>
							<div>
								<span className="text-muted-foreground">Inquiries</span>
								<p className="font-medium text-foreground">
									{property?.inquiries || 0}
								</p>
							</div>
						</div>

						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => onPropertyAction(property, "view")}
								className="flex-1"
							>
								<Eye className="w-4 h-4 mr-2" />
								View
							</Button>
							{canEdit && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => onPropertyAction(property, "edit")}
									className="flex-1"
								>
									<Edit className="w-4 h-4 mr-2" />
									Edit
								</Button>
							)}
							<Button
								variant="outline"
								size="sm"
								onClick={() => onPropertyAction(property, "chat")}
								className="flex-1"
							>
								<MessageCircle className="w-4 h-4 mr-2" />
								Chat
							</Button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default SalesTable;
